<template>
    <div class="d-flex flex-column" style="height: 48em">
        <!-- Header -->
        <header class="flex-shrink-0 p-3 border-bottom">
            <h2 class="editor-header">{{ $t('invoice_history.credit_note') }}</h2>
        </header>

        <!-- Content -->
        <main class="flex-grow-1 overflow-auto p-3" v-if="creditNote">
            <div class="row mb-2">
                <div class="col-sm-3"><span class="text-muted">{{ $t('common.id') }}</span>
                    <div>{{ creditNote.id }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.date') }}</span>
                    <div>{{ formatDate(creditNote.created) }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.status') }}</span>
                    <div>
                        <span :class="statusClass(creditNote.status)">{{ getStatusText(creditNote.status) }}</span>
                    </div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.sum') }}</span>
                    <div>{{ formatCurrency(creditNote.total_sum / 100) }}</div>
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.contractor_number') }}</span>
                    <div>{{ creditNote.contractor_number || '-' }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.gross_amount') }}</span>
                    <div>{{ formatGrossAmount(creditNote.gross_amount) }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.document_date') }}</span>
                    <div>{{ formatDate(creditNote.document_date) }}</div>
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-sm-3"><span class="text-muted">{{ $t('invoice_history.contract') }}</span>
                    <div>{{ invoice.contract.name }}</div>
                </div>
                <div class="col-sm-3"><span class="text-muted">{{ $t('credit_note.original_invoice') }}</span>
                    <div>{{ $t('common.id') }} {{ creditNote.invoice_id }}</div>
                </div>
                <div v-if="creditNote.info" class="col-sm-6">
                    <span class="text-muted">{{ $t('credit_note.info') }}</span>
                    <div>{{ creditNote.info }}</div>
                </div>
            </div>
            <div class="row mb-2">
                <div class="col-12">
                    <span class="text-muted">{{ $t('contracts.attachments') }}</span>
                    <div class="mt-1">
                        <span v-if="creditNoteAttachments.length === 0" class="text-muted small">{{ $t('daily_summary.no_credit_note_attachments') }}</span>
                        <span
                            v-for="attachment in creditNoteAttachments"
                            :key="attachment.id"
                            class="me-3"
                            style="cursor:pointer; color:#0d6efd; text-decoration:underline; font-size:.9em"
                            @click="downloadAttachment(attachment)"
                        >
                            {{ attachment.file_original_name || attachment.filename || 'Unnamed file' }}
                        </span>
                    </div>
                </div>
            </div>
            <hr/>
            <h6 class="mb-3"><i class="fas fa-list me-2"></i>{{ $t('invoice_history.invoice_rows') }}</h6>
            <div v-if="creditNoteRows && creditNoteRows.length > 0"
                 class="table-responsive">
                <table class="table table-sm">
                    <thead>
                    <tr>
                        <th>{{ $t('credit_note.row_id') }}</th>
                        <th>{{ $t('orders.order') }}</th>
                        <th>{{ $t('common.time_range') }}</th>
                        <th>{{ $t('account.account') }}</th>
                        <th>{{ $t('account.invoicing_code') }}</th>
                        <th>{{ $t('material_reports.unit') }}</th>
                        <th class="text-end">{{ $t('credit_note.credit_sum') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="o in creditNoteRows" :key="o.order_key">
                        <td>{{ o.invoice_row_id }}</td>
                        <td>{{ getInvoiceRowOrder(o.invoice_row_id, invoice) }}</td>
                        <td>{{ getInvoiceRowDates(o.invoice_row_id, invoice) }}</td>
                        <td>{{ getInvoiceRowAccount(o.invoice_row_id, invoice) }}</td>
                        <td>{{ getInvoiceRowInvoicingCode(o.invoice_row_id, invoice) }}</td>
                        <td>{{ pricingModelToText(o.pricing_model) }}</td>
                        <td class="text-end">{{ formatCurrency(o.sum / 100) }}</td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr class="fw-bold">
                        <td colspan="6" class="text-bold">{{ $t('credit_note.total') }}</td>
                        <td class="text-end text-bold">{{ formatCurrency(creditNote.total_sum / 100) }}</td>
                    </tr>
                    </tfoot>
                </table>
            </div>
        </main>
        <!-- Footer -->
        <footer class="flex-shrink-0 p-3 border-top">
            <div class="col-sm-12 my2 d-flex justify-content-end">
                <button class="btn btn-secondary form-button" @click.stop="$emit('close')">
                    {{ $t('common.close') }}
                </button>
            </div>
        </footer>
    </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {timeUtils} from '../mixins/TimeUtils'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {priceHelper} from "../mixins/PricingMixin.js";
import {invoicingHelper} from "../mixins/InvoicingMixin.js";
import {attachmentHelper} from "../mixins/AttachmentMixin.js";
import {downloadHelper} from "../mixins/DownloadMixin.js";

export default {
    name: 'CreditNoteDetails',
    components: {FontAwesomeIcon},
    mixins: [restApi, timeUtils, priceHelper, invoicingHelper, attachmentHelper, downloadHelper],
    props: {
        invoice: {type: Object, required: true},
        creditNoteId: {type: Number, required: true}
    },
    data() {
        return {
            creditNote: null,
            loading: false
        }
    },
    computed: {
        creditNoteAttachments() {
            return this.creditNote?.attachments || []
        },
        creditNoteRows() {
            const temp = []
            this.creditNote.credit_note_rows?.forEach( row => {
                const invoiceRow = this.invoice.invoice_rows.find(invRow => invRow.id === row.invoice_row_id)
                if (invoiceRow && invoiceRow.items && invoiceRow.items.length > 0) {
                    const order = invoiceRow.items[0].order
                    if (order) {
                        row.order = order
                        row.pricing_model = invoiceRow.items[0].pricing_model
                        temp.push(row)
                    }
                }
            })
            return temp
        }
    },
    created() {
          this.creditNote = this.invoice.credit_notes.find(note => note.id === this.creditNoteId)
    },
    methods: {
        downloadAttachment(attachment) {
            this.loading = true
            this.restFetch(
                this.contractAttachmentUrl + this.invoice.contract.id + '/' + attachment.id,
                response => {
                    const fileName = attachment.file_original_name || attachment.filename || 'download'
                    this.saveFile(fileName, response.data)
                    this.loading = false
                }, () => {
                    this.loading = false
                })
        }
    }
}
</script>

<style scoped>
.badge {
    padding: 0.4em 0.6em;
    font-size: 0.85em;
}

/* Remove grey background from table headers */
:deep(.routa-table-header) {
    background-color: transparent !important;
}

/* Add unified border below header row */
:deep(.bh-table-compact thead tr) {
    border-bottom: 1px solid #dee2e6 !important;
}

/* Remove grey background from table body */
:deep(.bh-table-compact) {
    background-color: white !important;
}

:deep(.bh-table-compact tbody) {
    background-color: white !important;
}

:deep(.bh-table-compact tr) {
    background-color: white !important;
}

:deep(.bh-table-responsive .dataTable-bottom .dataTable-info) {
    display: none !important;
}

.type-badge {
    display: inline-block;
    padding: 0.25em 0.6em;
    font-size: 0.85em;
    border: 1px solid #ccc;
    border-radius: 6px;
}

.credit-note-text {
    color: #003956 !important;
    font-style: italic;
}

.cn-badge {
    background-color: #fff3cd;
    border-color: #ffc107;
    color: #856404;
}

</style>
